import { ArrowCircleUp } from '@phosphor-icons/react';
import { useEffect, useRef } from 'react';
import { useChatStore } from '../../../shared/store/chatStore';

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSendMessage: (content: string) => void;
  isLoading: boolean;
}

export function ChatInput({ value, onChange, onSendMessage, isLoading }: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { isDrawerOpen } = useChatStore();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!value.trim() || isLoading) return;
    onSendMessage(value);
    onChange(''); // Clear the input after sending
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const adjustHeight = () => {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    };

    textarea.addEventListener('input', adjustHeight);
    adjustHeight(); // Initial adjustment

    return () => textarea.removeEventListener('input', adjustHeight);
  }, [value]);

  return (
    <div className="bg-[#ffffff] border-t border-[rgba(0,0,0,0.1)] p-4 z-50 relative">
      <div className="max-w-2xl mx-auto">
        <form onSubmit={handleSubmit} className="flex gap-2 relative">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            className="flex-1 bg-[#fff] px-5 py-3 pr-[70px] rounded-[25px] border-[#FF5C3C] border-2 border focus:outline-none min-h-[50px] max-h-[200px] resize-none overflow-y-auto"
            disabled={isLoading}
            rows={1}
          />
          <button
            type="submit"
            disabled={isLoading}
            className={`w-[40px] h-[40px] p-0 absolute top-[5px] right-[5px] ${
              isLoading
                ? "opacity-50 cursor-not-allowed"
                : "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            }`}
          >
            <ArrowCircleUp size={40} weight="fill" />
          </button>
        </form>
      </div>
    </div>
  );
} 